import { Wallet, PieChart, Landmark, ArrowRight, Download } from "lucide-react"
import { Button } from "../../components/ui/button"

export default function Finance() {
    return (
        <div className="min-h-screen bg-background font-sans text-foreground antialiased">
            {/* Hero Section - Standard (ref: DroitEcoGestion) */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Administration</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Direction Financière</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">Rigueur &</span>
                            <span className="block text-white mt-2">Transparence</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                La direction financière assure la gestion saine des ressources de l'université pour garantir un environnement d'apprentissage d'excellence.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                Consulter les Tarifs
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                            <Button variant="ghost" className="text-white border-2 border-white/20 hover:bg-white/10 hover:border-white px-10 py-7 text-sm font-black rounded-xl backdrop-blur-sm">
                                Télécharger le RIB
                                <Download className="w-5 h-5 ml-3" />
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Quick Stats Bar */}
            <div className="border-y border-border bg-secondary/10">
                <div className="container py-4 flex flex-wrap items-center gap-8">
                    {[
                        { val: "BCB", label: "Banque Partenaire" },
                        { val: "IBB", label: "Interbank Burundi" },
                        { val: "ECO", label: "Ecobank Partner" },
                        { val: "SEC", label: "Paiements Sécurisés" }
                    ].map((stat, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <span className="text-xl font-black text-primary">{stat.val}</span>
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</span>
                            {i < 3 && <span className="hidden lg:block text-border ml-4 font-light">/</span>}
                        </div>
                    ))}
                </div>
            </div>

            <article className="container section-padding-sm">
                <div className="grid lg:grid-cols-[250px_1fr] gap-12 lg:gap-20">
                    {/* Sidebar */}
                    <aside className="lg:sticky lg:top-8 lg:self-start hidden lg:block">
                        <nav className="space-y-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground mb-6 pb-2 border-b border-border">
                                Services
                            </p>
                            {["Comptabilité", "Budget", "Scolarité", "Bourses"].map((item, i) => (
                                <div key={i} className="flex items-start gap-3 py-2 text-xs font-bold text-muted-foreground">
                                    <span className="text-accent font-black">0{i + 1}</span>
                                    {item}
                                </div>
                            ))}
                        </nav>
                    </aside>

                    {/* Main Content */}
                    <main className="space-y-16">
                        <section>
                            <h2 className="heading-4 text-foreground mb-8 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Nos Missions Financières
                            </h2>
                            <div className="grid md:grid-cols-3 gap-6">
                                {[
                                    { title: "Trésorerie", icon: Wallet, desc: "Gestion quotidienne des flux financiers et des paiements fournisseurs." },
                                    { title: "Contrôle", icon: PieChart, desc: "Élaboration et suivi rigoureux du budget annuel de l'institution." },
                                    { title: "Scolarité", icon: Landmark, desc: "Accompagnement des étudiants dans le règlement de leurs frais." }
                                ].map((service, i) => (
                                    <div key={i} className="interactive-card p-6 border-l-4 border-primary transition-smooth">
                                        <service.icon className="w-8 h-8 text-primary mb-4 opacity-50" />
                                        <h3 className="text-sm font-bold uppercase tracking-tight mb-2">{service.title}</h3>
                                        <p className="text-xs text-muted-foreground leading-relaxed">{service.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="bg-primary/5 p-8 border border-primary/10 rounded-md">
                            <h3 className="heading-4 text-primary mb-6">Coordonnées Bancaires</h3>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="bg-background p-5 border border-border rounded-sm shadow-sm group hover:border-accent transition-smooth">
                                    <div className="flex justify-between items-start mb-3">
                                        <span className="text-[10px] font-black text-accent uppercase tracking-widest">BCB</span>
                                        <Button variant="ghost" size="icon" className="h-6 w-6"><Download className="w-3 h-3" /></Button>
                                    </div>
                                    <h4 className="text-sm font-bold text-foreground">Banque de Crédit de Bujumbura</h4>
                                    <p className="text-xs text-muted-foreground mt-1 courier">Compte n° 123-456789-01</p>
                                </div>
                                <div className="bg-background p-5 border border-border rounded-sm shadow-sm group hover:border-accent transition-smooth">
                                    <div className="flex justify-between items-start mb-3">
                                        <span className="text-[10px] font-black text-accent uppercase tracking-widest">IBB</span>
                                        <Button variant="ghost" size="icon" className="h-6 w-6"><Download className="w-3 h-3" /></Button>
                                    </div>
                                    <h4 className="text-sm font-bold text-foreground">Interbank Burundi</h4>
                                    <p className="text-xs text-muted-foreground mt-1 courier">Compte n° 987-654321-02</p>
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </article>
        </div>
    )
}
